import { ShoppingCart, TrendingUp, Users, Star, ArrowRight } from "lucide-react";
import { IndustryPageLayout } from "@/components/IndustryPageLayout";
import { Card, CardContent } from "@/components/ui/card";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Tables } from "@/integrations/supabase/types";

type IndustryImage = Tables<'industry_images'>;

const EcommercePage = () => {
  const [industryImages, setIndustryImages] = useState<IndustryImage[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchIndustryImages();
  }, []);

  const fetchIndustryImages = async () => {
    try {
      const { data, error } = await supabase
        .from('industry_images')
        .select('*')
        .eq('industry', 'ecommerce')
        .order('display_order', { ascending: true });

      if (error) throw error;
      setIndustryImages(data || []);
    } catch (error) {
      console.error('Error fetching industry images:', error);
    } finally {
      setLoading(false);
    }
  };

  const industry = {
    title: "E-commerce",
    icon: ShoppingCart,
    gradient: "from-emerald-500 to-teal-500",
    description: "Transform your online store with AI-generated product images, lifestyle shots, and marketing visuals that drive conversions and boost sales.",
    stats: "40% increase in CTR"
  };

  const heroContent = (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <Card className="bg-white/5 backdrop-blur-md border-white/10">
        <CardContent className="p-6 text-center">
          <TrendingUp className="w-8 h-8 text-emerald-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">Boost Conversions</h3>
          <p className="text-gray-300 text-sm">High-quality product images increase conversion rates by up to 40%</p>
        </CardContent>
      </Card>
      <Card className="bg-white/5 backdrop-blur-md border-white/10">
        <CardContent className="p-6 text-center">
          <Users className="w-8 h-8 text-emerald-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">Engage Customers</h3>
          <p className="text-gray-300 text-sm">Lifestyle images help customers visualize products in their lives</p>
        </CardContent>
      </Card>
      <Card className="bg-white/5 backdrop-blur-md border-white/10">
        <CardContent className="p-6 text-center">
          <Star className="w-8 h-8 text-emerald-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">Build Trust</h3>
          <p className="text-gray-300 text-sm">Professional imagery builds brand credibility and customer trust</p>
        </CardContent>
      </Card>
    </div>
  );

  const mainContent = (
    <div className="prose prose-invert max-w-none">
      <h2 className="text-3xl font-bold text-white mb-6">Revolutionizing E-commerce with AI-Generated Visuals</h2>
      
      <div className="text-gray-300 space-y-6 text-lg leading-relaxed">
        <p>
          In the competitive world of e-commerce, visual content is everything. Your product images are often the first and most important interaction customers have with your brand. With AI-generated imagery, you can create stunning, professional-quality visuals that not only showcase your products but tell a compelling story.
        </p>
        
        <h3 className="text-2xl font-semibold text-white mt-8 mb-4">The E-commerce Visual Challenge</h3>
        <p>
          Traditional product photography is expensive, time-consuming, and inflexible. Every new product requires a new photoshoot, seasonal campaigns need constant updates, and A/B testing different visual styles can break the budget. This is where AI transforms the game.
        </p>
        
        <h3 className="text-2xl font-semibold text-white mt-8 mb-4">AI-Powered Solutions for Modern Retailers</h3>
        <ul className="list-disc list-inside space-y-3 text-gray-300">
          <li><strong className="text-white">Product Visualization:</strong> Generate multiple angles, colors, and variations instantly</li>
          <li><strong className="text-white">Lifestyle Integration:</strong> Place products in realistic environments and scenarios</li>
          <li><strong className="text-white">Seasonal Campaigns:</strong> Quickly adapt visuals for holidays, seasons, and trends</li>
          <li><strong className="text-white">A/B Testing:</strong> Create multiple visual variations to optimize performance</li>
          <li><strong className="text-white">Scale Efficiently:</strong> Generate thousands of images for large catalogs</li>
        </ul>
        
        <h3 className="text-2xl font-semibold text-white mt-8 mb-4">Success Stories & Results</h3>
        <p>
          Leading e-commerce brands using AI-generated visuals report average improvements of 40% in click-through rates, 25% increase in time spent on product pages, and 30% boost in overall conversion rates. The ability to rapidly test and iterate visual content has become a competitive advantage.
        </p>
        
        <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-6 mt-8">
          <h4 className="text-xl font-semibold text-emerald-400 mb-3 flex items-center">
            <ArrowRight className="w-5 h-5 mr-2" />
            Ready to Get Started?
          </h4>
          <p className="text-gray-300">
            Transform your e-commerce visuals today. Generate product images, lifestyle shots, and marketing materials that drive sales and engage customers.
          </p>
        </div>
      </div>
    </div>
  );

  const imageGallery = (
    <div>
      <h3 className="text-2xl font-bold text-white text-center mb-8">AI-Generated E-commerce Examples</h3>
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((index) => (
            <Card key={index} className="bg-white/5 backdrop-blur-md border-white/10 overflow-hidden animate-pulse">
              <div className="aspect-square bg-gradient-to-br from-emerald-500/20 to-teal-500/20"></div>
              <CardContent className="p-4">
                <div className="h-4 bg-white/10 rounded mb-2"></div>
                <div className="h-3 bg-white/5 rounded"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : industryImages.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {industryImages.map((image) => (
            <Card key={image.id} className="bg-white/5 backdrop-blur-md border-white/10 overflow-hidden">
              <div className="aspect-square bg-gradient-to-br from-emerald-500/20 to-teal-500/20 flex items-center justify-center overflow-hidden">
                <img
                  src={image.image_url}
                  alt={image.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    const fallbackDiv = document.createElement('div');
                    fallbackDiv.className = 'flex items-center justify-center w-full h-full';
                    
                    // Create SVG element safely without innerHTML
                    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
                    svg.setAttribute('class', 'w-12 h-12 text-emerald-400');
                    svg.setAttribute('fill', 'none');
                    svg.setAttribute('stroke', 'currentColor');
                    svg.setAttribute('viewBox', '0 0 24 24');
                    
                    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
                    path.setAttribute('stroke-linecap', 'round');
                    path.setAttribute('stroke-linejoin', 'round');
                    path.setAttribute('stroke-width', '2');
                    path.setAttribute('d', 'M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z');
                    
                    svg.appendChild(path);
                    fallbackDiv.appendChild(svg);
                    e.currentTarget.parentElement!.appendChild(fallbackDiv);
                  }}
                />
              </div>
              <CardContent className="p-4">
                <h4 className="text-sm font-medium text-white mb-1">{image.title}</h4>
                <p className="text-sm text-gray-300">{image.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((index) => (
            <Card key={index} className="bg-white/5 backdrop-blur-md border-white/10 overflow-hidden">
              <div className="aspect-square bg-gradient-to-br from-emerald-500/20 to-teal-500/20 flex items-center justify-center">
                <ShoppingCart className="w-12 h-12 text-emerald-400" />
              </div>
              <CardContent className="p-4">
                <p className="text-sm text-gray-300">Product visualization example {index}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <IndustryPageLayout
      industry={industry}
      seo={{
        title: "AI Product Photography for E-commerce | Boost Sales 40%",
        description: "Generate stunning AI product photos for your online store. Increase conversions with professional e-commerce images. Perfect for Amazon, Shopify, Etsy sellers.",
        keywords: "ai product photos, e-commerce images, product photography ai, amazon product images, shopify photos, etsy product photography",
        url: "/industries/ecommerce"
      }}
      heroContent={heroContent}
      mainContent={mainContent}
      imageGallery={imageGallery}
    />
  );
};

export default EcommercePage;
